import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import api from '../../services/api';
import { MEMBER_GROUP_NAV_LINKS } from '../../constants/memberGroups';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'Research', path: '/research' },
  { label: 'Funding Sources', path: '/funding-sources' },
  { label: 'Members', path: '/members' },
  { label: 'Publications', path: '/publications' },
  { label: 'News', path: '/news' },
  { label: 'Recruitment', path: '/recruitment' },
  { label: 'Contact', path: '/contact' },
];

type MegaMenuItem = {
  label: string;
  to?: string;
};

type PublicationNavItem = { type?: 'journal' | 'patent' | 'other' };

const publicationTypeOrder: Array<{
  key: 'journal' | 'patent' | 'other';
  label: string;
}> = [
  { key: 'journal', label: 'Journal Articles' },
  { key: 'patent', label: 'Patents' },
  { key: 'other', label: 'Other Outputs' },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedPath, setMobileExpandedPath] = useState<string | null>(null);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [publicationTypes, setPublicationTypes] = useState<
    Array<'journal' | 'patent' | 'other'>
  >([]);

  useEffect(() => {
    api
      .get<PublicationNavItem[]>('/publications')
      .then((res) => {
        const set = new Set(
          (res.data || [])
            .map((p) => p.type)
            .filter(
              (t): t is 'journal' | 'patent' | 'other' =>
                t === 'journal' || t === 'patent' || t === 'other'
            )
        );
        setPublicationTypes(Array.from(set));
      })
      .catch(() => setPublicationTypes([]));
  }, []);

  const memberMenu: MegaMenuItem[] = MEMBER_GROUP_NAV_LINKS;

  const publicationMenu = useMemo<MegaMenuItem[]>(() => {
    const hasCurrent = publicationTypes.length > 0;
    return publicationTypeOrder
      .filter((t) => (hasCurrent ? publicationTypes.includes(t.key) : true))
      .map((t) => ({ label: t.label, to: `/publications?type=${encodeURIComponent(t.key)}` }));
  }, [publicationTypes]);

  const megaMenuMap: Record<string, MegaMenuItem[]> = {
    '/members': memberMenu,
    '/publications': publicationMenu,
    '/news': [
      { label: 'Activities', to: '/news?category=activity' },
      { label: 'Conferences', to: '/news?category=conference' },
      { label: 'Graduation', to: '/news?category=graduation' },
    ],
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface flex flex-col">
      <header className="sticky top-0 z-50 bg-um-navy text-white shadow-md shadow-blue-950/20">
        <div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3 text-white">
            <div>
              <div className="font-serif text-xl italic leading-tight">
                Catalysis for Green Energy
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => setHoveredPath(item.path)}
                onMouseLeave={() => setHoveredPath((prev) => (prev === item.path ? null : prev))}
              >
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    [
                      'relative block px-4 py-3 text-sm font-semibold transition-colors',
                      'hover:bg-um-navySoft',
                      isActive ? 'text-um-gold' : 'text-blue-50',
                    ].join(' ')
                  }
                >
                  {item.label}
                  <span
                    className={`absolute inset-x-0 bottom-0 h-0.5 transition-opacity ${
                      hoveredPath === item.path ? 'bg-um-gold opacity-100' : 'opacity-0'
                    }`}
                  />
                </NavLink>

                {hoveredPath === item.path && megaMenuMap[item.path]?.length > 0 && (
                  <div className="absolute left-0 top-full z-50 min-w-[220px] overflow-hidden rounded-sm bg-um-navySoft/95 py-2 shadow-xl shadow-blue-950/30 backdrop-blur-sm">
                    {megaMenuMap[item.path].map((sub) =>
                      sub.to ? (
                        <Link
                          key={sub.label}
                          to={sub.to}
                          className="block px-4 py-2 text-sm text-blue-50 transition-colors hover:bg-um-navy hover:text-white"
                        >
                          {sub.label}
                        </Link>
                      ) : (
                        <div
                          key={sub.label}
                          className="cursor-pointer px-4 py-2 text-sm text-blue-50 transition-colors hover:bg-um-navy hover:text-white"
                        >
                          {sub.label}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <button className="inline-flex lg:hidden items-center justify-center rounded-sm bg-um-navySoft px-3 py-1 text-xs text-white" onClick={() => setMobileOpen((v) => !v)}>
            Menu
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div className="bg-um-navySoft px-6 py-3 space-y-2 text-sm lg:hidden text-blue-50">
          <div className="mx-auto max-w-screen-2xl space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className="space-y-1">
                <div className="flex items-center gap-1">
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      [
                        'block flex-1 rounded-sm px-2 py-1',
                        isActive ? 'bg-um-navyDeep text-white' : 'hover:bg-um-navy',
                      ]
                        .filter(Boolean)
                        .join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                  {megaMenuMap[item.path]?.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setMobileExpandedPath((prev) =>
                          prev === item.path ? null : item.path
                        )
                      }
                      className="rounded-sm px-2 py-1 text-xs text-blue-100 hover:bg-um-navy"
                    >
                      {mobileExpandedPath === item.path ? 'Collapse' : 'Expand'}
                    </button>
                  )}
                </div>
                {mobileExpandedPath === item.path &&
                  megaMenuMap[item.path]?.length > 0 && (
                    <div className="ml-3 space-y-1 border-l border-blue-200/20 pl-3">
                      {megaMenuMap[item.path].map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.to || item.path}
                          onClick={() => setMobileOpen(false)}
                          className="block rounded-sm px-2 py-1 text-xs text-blue-100 hover:bg-um-navy"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-um-navyDeep py-12 px-8 text-blue-100">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mb-8 flex items-center gap-3 text-white">
            <span className="font-serif text-xl italic">Catalysis for Green Energy</span>
          </div>
          <div>
            © 2026 Catalysis for Green Energy
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;

